/**
 * Custom hook for dashboard data using both GraphQL and REST APIs
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_DASHBOARD_DATA } from '../graphql/queries';
import { unifiedApi } from '../services/unifiedApi';

export interface DashboardData {
  stats: {
    totalOffers: number;
    activeOffers: number;
    processingOffers: number;
    totalProducts: number;
    totalMerchants: number;
    avgConfidence: number;
  };
  recentOffers: any[];
  recentProducts: any[];
  merchants: any[];
  cities: any[];
}

export const useDashboardData = (useGraphQL: boolean = true) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // GraphQL query
  const {
    data: graphqlData,
    loading: graphqlLoading,
    error: graphqlError,
    refetch: refetchGraphQL
  } = useQuery(GET_DASHBOARD_DATA, {
    skip: !useGraphQL,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  // Define loadDataWithRest before using it in useEffect
  const loadDataWithRest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const dashboardData = await unifiedApi.getDashboardData();
      
      setData({
        stats: {
          ...dashboardData.stats,
          processingOffers: 0, // Would need to calculate from REST data
        },
        recentOffers: dashboardData.recentOffers,
        recentProducts: dashboardData.recentProducts,
        merchants: dashboardData.merchants,
        cities: dashboardData.cities,
      });
    } catch (err) {
      console.error('Dashboard data loading failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  // REST API fallback
  useEffect(() => {
    if (!useGraphQL || (graphqlError && !graphqlData)) {
      loadDataWithRest();
    }
  }, [useGraphQL, graphqlError, graphqlData, loadDataWithRest]);

  // Transform GraphQL data when available
  useEffect(() => {
    if (graphqlData && useGraphQL) {
      try {
        const transformedData: DashboardData = {
          stats: {
            totalOffers: graphqlData.dashboardStats?.offers?.totalOffers || 0,
            activeOffers: graphqlData.dashboardStats?.offers?.activeOffers || 0,
            processingOffers: graphqlData.dashboardStats?.offers?.processingOffers || 0,
            totalProducts: graphqlData.dashboardStats?.products?.totalProducts || 0,
            totalMerchants: graphqlData.dashboardStats?.merchants?.totalMerchants || 0,
            avgConfidence: Math.round((graphqlData.dashboardStats?.products?.avgConfidence || 0) * 100),
          },
          recentOffers: transformOffersFromGraphQL(graphqlData.recentOffers || []),
          recentProducts: transformProductsFromGraphQL(graphqlData.recentProducts || []),
          merchants: transformMerchantsFromGraphQL(graphqlData.merchants || []),
          cities: transformCitiesFromGraphQL(graphqlData.cities || []),
        };

        setData(transformedData);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error transforming GraphQL data:', err);
        setError('Failed to process GraphQL data');
        // Fallback to REST
        loadDataWithRest();
      }
    }
  }, [graphqlData, useGraphQL, loadDataWithRest]);

  // Set loading state for GraphQL
  useEffect(() => {
    if (useGraphQL) {
      setLoading(graphqlLoading);
      if (graphqlError) {
        setError(graphqlError.message);
      }
    }
  }, [graphqlLoading, graphqlError, useGraphQL]);

  const refetch = async () => {
    if (useGraphQL) {
      return refetchGraphQL();
    } else {
      return loadDataWithRest();
    }
  };

  return {
    data,
    loading,
    error,
    refetch,
    isUsingGraphQL: useGraphQL && !graphqlError,
  };
};

// Transform functions (similar to UnifiedApiService)
function transformOffersFromGraphQL(offers: any[]): any[] {
  return offers.map((offer) => ({
    id: offer.id,
    title: offer.title,
    description: offer.description,
    status: offer.status?.toLowerCase() || 'draft',
    merchant_id: offer.merchant?.id,
    merchant_name: offer.merchant?.nameArabic || offer.merchantName,
    products_count: offer.productsCount || 0,
    views_count: offer.viewsCount || 0,
    start_date: offer.startDate,
    end_date: offer.validUntil,
    created_at: offer.createdAt,
    updated_at: offer.updatedAt,
    images_count: offer.images?.length || 0,
  }));
}

function transformProductsFromGraphQL(products: any[]): any[] {
  return products.map((product) => ({
    id: product.id,
    confidence: product.confidence || 0.9,
    confidence_score: product.confidence || 0.9,
    image_url: product.imageUrl,
    created_at: product.createdAt,
    updated_at: product.updatedAt,
    extracted_data: {
      product_name: product.extractedData?.productName || 'منتج غير محدد',
      brand: product.extractedData?.brand,
      price_old: product.extractedData?.priceOld,
      price_new: product.extractedData?.priceNew,
      discount: product.extractedData?.discount || 0,
      quantity: product.extractedData?.quantity,
      category: product.extractedData?.category,
    },
  }));
}

function transformMerchantsFromGraphQL(merchants: any[]): any[] {
  return merchants.map((merchant) => ({
    id: merchant.id,
    name_arabic: merchant.nameArabic,
    name: merchant.nameArabic,
    logo_url: merchant.logoUrl,
    is_active: merchant.isActive,
  }));
}

function transformCitiesFromGraphQL(cities: any[]): any[] {
  return cities.map((city) => ({
    id: city.id,
    name_arabic: city.nameArabic,
    name_english: city.nameEnglish,
    region: city.region,
  }));
}